import { useEffect, useMemo, useRef, useState } from "react";
import { Trophy } from "lucide-react";
import { playFanfare, playTick, playDing } from "../lib/sounds";
import { useLang } from "../i18n";
import { FortuneWheel } from "./FortuneWheel";
import { SoundControls } from "./SoundControls";

// Estrazione con vera ruota della fortuna: gira per durationMs con ease-out
// marcato (giri veloci a lungo, finale drammatico) e la lancetta si ferma
// esattamente sullo spicchio del vincitore. Il tick scatta a ogni spicchio
// che passa sotto la lancetta: sincronizzato per costruzione con la ruota.
// La LANCETTA e' simulata come vera molla-smorzatore (spring-damper integrato
// a ogni frame con semi-implicit Euler): la bacchetta le da' un impulso mentre
// le passa sotto (ancora mobile = posizione della bacchetta, molla rigida),
// al superamento l'ancora torna a zero e la lancetta SCATTA indietro con
// vibrazione sotto-smorzata organica — mai pre-calcolata, sempre fisica vera.
// Chi apre la pagina a estrazione iniziata si sincronizza su startedAt.
export function GiveawayWheel({ names, winner, startedAt, durationMs = 30000, soundOn = true, onToggleSound, onDone }) {
  const { t } = useLang();
  const winnerIndex = useMemo(() => Math.max(0, names.indexOf(winner)), [names, winner]);
  const [rotation, setRotation] = useState(0);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pointerBend, setPointerBend] = useState(0);
  const finishRef = useRef(false);
  const rootRef = useRef(null);
  const pointerRef = useRef({ a: 0, v: 0 }); // lancetta: angolo (deg) e velocita' angolare (deg/s)
  const prevTRef = useRef(null);             // timestamp frame precedente (per dt reale)
  // nome dello spicchio sotto la lancetta in questo istante (lettura in grande)
  const segAngle = names.length ? 360 / names.length : 360;
  const underPointer = names.length
    ? names[Math.floor(((((360 - (rotation % 360)) % 360) + 360) % 360) / segAngle) % names.length]
    : "";

  // all'avvio dell'estrazione porta la ruota al centro dello schermo
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  useEffect(() => {
    if (!names.length || !winner) return undefined;
    const n = names.length;
    const seg = 360 / n;
    const start = startedAt ? new Date(startedAt).getTime() : Date.now();
    // atterra in un punto casuale dentro lo spicchio del vincitore: a volte si
    // ferma appena oltre l'ultima bacchetta (fino a 0.85) e la lancetta resta piegata
    const offset = 0.2 + Math.random() * 0.65;
    const spins = 6 + Math.floor(Math.random() * 3); // 6-8 giri completi
    const finalRot = spins * 360 + (((360 - (winnerIndex + offset) * seg) % 360) + 360) % 360;
    let raf;
    let lastSeg = -1;
    // integrazione fisica della lancetta (molla-smorzatore, gradi):
    // la bacchetta in contatto e' l'ancora mobile della molla (spinta nel verso
    // del giro); appena la supera l'ancora torna a 0 e la lancetta vibra libera
    const stepPointer = (rotDeg) => {
      const nowMs = Date.now();
      const dt = Math.min(0.05, Math.max(0.001, (nowMs - (prevTRef.current ?? nowMs)) / 1000));
      prevTRef.current = nowMs;
      const segF = (((rotDeg % seg) + seg) % seg) / seg;
      const contact = segF > 0.72; // bacchetta sotto la punta: ultimo 28% dello spicchio
      const anchor = contact ? -26 * (segF - 0.72) / 0.28 : 0;
      const k = contact ? 340 : 190; // molla rigida sul contatto, morbida da libera
      const c = contact ? 36 : 5.2;  // smorzamento: libera = sotto-smorzata, vibra
      const pt = pointerRef.current;
      const acc = -k * (pt.a - anchor) - c * pt.v;
      pt.v += acc * dt;
      pt.a += pt.v * dt;
      pt.a = Math.max(-40, Math.min(14, pt.a)); // finecorsa meccanico del perno
      setPointerBend(pt.a);
    };
    const frame = () => {
      const p = Math.min(1, (Date.now() - start) / durationMs);
      setProgress(p);
      if (p >= 1) {
        const st = (Date.now() - start) / 1000 - durationMs / 1000;
        if (st < 0.9) {
          // assestamento fisico: piccola oscillazione smorzata prima del fermo totale
          const settleRot = finalRot + 1.6 * Math.sin(st * 22) * Math.exp(-5 * st);
          setRotation(settleRot);
          stepPointer(settleRot); // la lancetta segue l'assestamento della ruota
          raf = requestAnimationFrame(frame);
          return;
        }
        setRotation(finalRot);
        stepPointer(finalRot); // stato finale: appoggiata sulla bacchetta o a riposo
        if (!finishRef.current) {
          finishRef.current = true;
          setDone(true);
          if (soundOn) { playDing(); playFanfare(); }
          if (onDone) setTimeout(onDone, 6000); // celebrazione di 6 secondi, poi sparisce da sola
        }
        return;
      }
      const rot = finalRot * (1 - Math.pow(1 - p, 4)); // ease-out quartico
      setRotation(rot);
      stepPointer(rot);
      const s = Math.floor(rot / seg);
      if (s !== lastSeg) {
        if (lastSeg !== -1 && soundOn) playTick(1 - p); // un tick a ogni bacchetta superata
        lastSeg = s;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={rootRef}
      className={`chamfer border bg-[#050505] p-8 md:p-12 text-center relative overflow-hidden scanlines-bg transition-colors duration-500 ${done ? "border-manticore/60" : "border-wardogs/40"}`}
      data-testid="giveaway-wheel"
    >
      <style>{`
        @keyframes gw-pop { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes gw-fall { 0% { transform: translateY(-10px) rotate(0deg); opacity: 1; } 100% { transform: translateY(340px) rotate(540deg); opacity: 0; } }
        .gw-winner { animation: gw-pop 0.6s cubic-bezier(0.2, 1.4, 0.4, 1) both; }
        .gw-confetto { position: absolute; top: -10px; width: 8px; height: 14px; animation: gw-fall linear infinite; }
      `}</style>
      <div className={`absolute top-0 left-0 right-0 h-px ${done ? "bg-manticore" : "bg-wardogs"}`} />

      {done && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {Array.from({ length: 26 }).map((_, i) => (
            <span
              key={i}
              className="gw-confetto"
              style={{
                left: `${(i * 37) % 100}%`,
                backgroundColor: ["#FF5C00", "#3DDC84", "#F5F5F5", "#9146FF"][i % 4],
                animationDuration: `${1.8 + (i % 5) * 0.4}s`,
                animationDelay: `${(i % 7) * 0.18}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="absolute top-4 right-4 z-10">
        <SoundControls soundOn={soundOn} onToggle={onToggleSound} compact />
      </div>

      <p className="hud-label mb-6" data-testid="wheel-label">
        {done ? t("gv.winner_is") : t("gv.drawing")}
      </p>

      <FortuneWheel
        names={names}
        rotation={rotation}
        winnerIndex={winnerIndex}
        landed={done}
        size={680}
        pointerBend={pointerBend}
      />

      {done ? (
        <div className="mt-6 flex flex-col items-center gap-3">
          <p className="gw-winner font-display text-5xl md:text-7xl uppercase leading-none text-manticore" data-testid="wheel-winner-name">
            {winner}
          </p>
          <Trophy className="w-8 h-8 text-manticore" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-steel">{t("gv.winner_sub")}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4A4D51]" data-testid="wheel-fair-note">{t("gv.fair", { n: names.length })}</p>
        </div>
      ) : (
        <div className="mt-6 max-w-md mx-auto">
          {/* con tanti iscritti gli spicchi sono sottili: il nome sotto la lancetta appare in grande */}
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#4A4D51] mb-1">{t("gv.under_pointer")}</p>
          <p className="font-display text-4xl md:text-5xl uppercase leading-none text-white truncate mb-5" data-testid="wheel-under-pointer">
            {underPointer}
          </p>
          <div className="h-1 bg-[#1A1A1A] overflow-hidden" data-testid="wheel-progress">
            <div className="h-full bg-wardogs transition-[width] duration-150" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A4D51] mt-3">{t("gv.wheel_sub")}</p>
        </div>
      )}
    </div>
  );
}
